import React, { useState, useEffect } from "react";
import { Input, message } from "antd";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { HeadingOne, BodyOne } from "../components/typography";
import BlueButton from "../components/buttons/BlueButton";
import AuthForm from "../components/forms/AuthForm";
import { global_tailwind_constants, genders, validators } from "../constants";
import { CelluarInput, CheckBox } from "../components/inputs";
import SupportBlock from "../components/info/SupportBlock";
import Toggle from "../components/inputs/Switch";
import { environments } from "../constants";
import InputValidator from "../components/inputs/InputValidator";
import { SIGN_UP_DRIVER } from "../graphql/Mutations";
import { request } from "../utils/helpers";
import { useQueryParams } from "../hooks/useQueryParams";
import { parseError } from "../utils/parseError";
import { useLoginMutation, useSSOData } from "../hooks";
import mixpanelService from "../services/mixpanel";
import bugfenderHelper from "../services/bugfender";

const RedeemDriverInvite = () => {
  const { formState: { errors }, control, getValues } = useForm();
  const { validSSOData } = useSSOData();
  const values = getValues();

  const navigate = useNavigate();
  const [signUpMutation, { loading: signUpLoading, error: signUpError }] =
    useMutation(SIGN_UP_DRIVER);

  const [useMobile, setUseMobile] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [driverDetails, setDriverDetails] = useState({});

  const location = useLocation();
  const queryParams = useQueryParams(location.search);
  const orgId = queryParams.get("orgId");
  const inviteId = queryParams.get("inviteId");

  const { loading, handleLogin } = useLoginMutation({
    variable: undefined,
    ssoData: validSSOData,
    onCompletedCallback: () => {
      navigate(`/verification-step-one`);
    },
    canAddMember: true,
    addMemberPayload: {
      orgId,
      firstName: driverDetails.firstName,
      lastName: driverDetails.lastName,
      gender: values.gender,
      email: values?.email?.toLowerCase(),
      phoneNumber: driverDetails.username
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, error } = await request(
          `${environments.REACT_APP_ORG_V2}${environments.REACT_APP_ORG_V2_INVITE_REDEEM_DRIVER}`,
          {
            method: "PUT",
            body: {
              org_id: orgId,
              invite_id: inviteId,
            },
          }
        );

        if (error) {
          message.error(
            "Unable to redeem driver invite. The driver invitation may no longer be valid."
          );
          console.error("Error fetching data:", error);
        } else {
          setDriverDetails(data);
          message.success("Driver successfully redeemed invite");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [inviteId, orgId]);

  const onSubmit = async (e) => {
    const formInput = getValues();
    e.preventDefault();
    let hasErrors = false;

    if (useMobile && !driverDetails?.username) {
      message.error("Please enter your mobile number.");
      hasErrors = true;
    }

    if (!formInput.gender) {
      message.error("Please select your gender.");
      hasErrors = true;
    }

    if (!formInput.password) {
      message.error("Please enter your password.");
      hasErrors = true;
    }

    if (!useMobile && !formInput.email) {
      message.error("Please enter your email.");
      hasErrors = true;
    }

    if (!hasErrors) {
      const input = {
        email: formInput?.email?.toLowerCase(),
        gender: formInput.gender,
        firstName: driverDetails.firstName,
        lastName: driverDetails.lastName,
        password: formInput.password,
        phoneNumber: driverDetails?.username,
        isAdmin: true,
        sendNotification: false,
        group: "Driver",
      };

      signUpMutation({
        variables: { input },
        onCompleted: (data) => {
          if (data?.signUpDriver?.username) {
            message.success("Driver successfully signed up");
            mixpanelService.people.set({
              username: useMobile ? driverDetails?.username : formInput.email?.toLowerCase(),
              firstName: driverDetails.firstName,
              lastName: driverDetails.lastName,
              group: "Driver",
            });
            handleLogin(
              useMobile ? driverDetails?.username : formInput.email?.toLowerCase(),
              formInput.password
            );
          } else {
            message.error("Driver already signed up");
            setIsLoading(false);
          }
        },
        onError: (error) => {
          setIsLoading(false);
          console.error("Error redeeming driver invite:", error);
          bugfenderHelper.error(error);
        },
      });
    }
  };

  useEffect(() => {
    if (!signUpError) return;

    const processSignUpError = async () => {
      try {
        const errorMessage = parseError(signUpError);
        message.error(errorMessage);
        bugfenderHelper.error(errorMessage);
      } catch (err) {
        bugfenderHelper.error(err);
        console.error("signUpMutation [ERROR]\n", err);
        message.error("We've encountered an issue with your registration.");
      }
    };

    processSignUpError();
  }, [signUpError]);

  return (
    <div>
      <form onSubmit={onSubmit}>
        <header>
          <HeadingOne className="text-tripplo-200 text-center mt-4 mb-4">
            Create Account
          </HeadingOne>
        </header>
        <div className="flex justify-center">
          <div className="grid grid-rows-2 place-content-between gap-20">
            <div className={global_tailwind_constants.formWidth}>
              <AuthForm
                active={true}
                tailwindProps={global_tailwind_constants.formWidth}
              >
                <div className="flex flex-row p-2 justify-between ">
                  <BodyOne className="mb-2 font-semibold">
                    Select Preferred Sign In Method
                  </BodyOne>
                  <Controller
                    name="sign_in_method"
                    control={control}
                    render={({ field }) => (
                      <Toggle
                        onChange={() => setUseMobile(!useMobile)}
                        checked={useMobile}
                        checkedLabel="Mobile Number"
                        unCheckedLabel="Email Address"
                      />
                    )}
                  />
                </div>
                <div className="flex flex-col p-2 mb-3">
                  {!useMobile && (
                    <div className="flex flex-col p-2 ">
                      <BodyOne className="mb-2">Email address</BodyOne>
                      <Controller
                        name="email"
                        control={control}
                        rules={{ required: true }}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Input
                            size="large"
                            placeholder="Enter your email address here"
                            onChange={onChange}
                            value={value}
                          />
                        )}
                      />
                      {errors.email && (
                        <BodyOne className="text-red-600">
                          Please enter your email
                        </BodyOne>
                      )}
                    </div>
                  )}
                  {useMobile && (
                    <div className="flex flex-col p-2 ">
                      <BodyOne className="mb-2 ">Mobile number</BodyOne>
                      <Controller
                        name="phone_number"
                        control={control}
                        defaultValue={
                          driverDetails?.username ? driverDetails.username : ""
                        }
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                          <CelluarInput
                            onChange={onChange}
                            value={value}
                            defaultValue={
                              driverDetails?.username
                                ? driverDetails.username
                                : "loading"
                            }
                            placeholder="Fetching phone number, please wait..."
                            disabled={true}
                          />
                        )}
                      />

                      {errors.phone_number && !driverDetails?.username && (
                        <BodyOne className="text-red-600">
                          Please enter your mobile number
                        </BodyOne>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col p-2">
                    <BodyOne className="mb-2">Gender</BodyOne>

                    <div className="flex justify-between">
                      <Controller
                        name="gender"
                        control={control}
                        defaultValue=""
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                          <>
                            {genders.map((gender) => (
                              <div key={gender.id} className="flex flex-row">
                                <CheckBox
                                  className="mt-1 mr-2"
                                  name={gender.value}
                                  onChange={() => onChange(gender.value)}
                                  value={value}
                                />
                                <span>{gender.label}</span>
                              </div>
                            ))}
                          </>
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col p-2 ">
                    <BodyOne className="mb-2" isRequired={true}>
                      Password
                    </BodyOne>
                    <Controller
                      name="password"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: true,
                        validate: (value) => validators.isPasswordValid(value),
                      }}
                      render={({ field: { onChange, value } }) => (
                        <InputValidator
                          validatorStates={validators.getPasswordValidatorStates(
                            value
                          )}
                          iconTitles={["8+ Letters", "1 Uppercase", "1 Number"]}
                        >
                          <Input.Password
                            style={{ border: "1px solid transparent" }}
                            size="large"
                            placeholder="Enter your password here"
                            onChange={onChange}
                            value={value}
                          />
                        </InputValidator>
                      )}
                    />
                    {errors.password && (
                      <BodyOne className="text-red-600">
                        {errors.password.message}
                      </BodyOne>
                    )}
                  </div>

                  <SupportBlock area="login" />
                </div>
              </AuthForm>
            </div>
            <div className="flex flex-col">
              <BodyOne className="text-center">
                By continuing you agree <br /> to our{" "}
                <Link
                  className="text-tripplo-200 hover:text-tripplo-200"
                  to="/legal/terms-of-use"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  className="text-tripplo-200 hover:text-tripplo-200"
                  to="/legal/privacy-policy"
                >
                  Privacy Policy
                </Link>
              </BodyOne>

              <BlueButton
                id="loginButton"
                className="w-full mt-4"
                isLoading={signUpLoading || isLoading || loading}
                disabled={signUpLoading || isLoading || loading}
              >
                Proceed
              </BlueButton>
              <BodyOne className="text-center mt-2">
                Already have an account?{" "}
                <span
                  className="text-tripplo-200 font-medium cursor-pointer"
                  onClick={() =>
                    navigate(
                      "/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM="
                    )
                  }
                >
                  Login
                </span>
              </BodyOne>
              <BodyOne className="text-center">
                Would you like to remove your account ?<br></br>
                <a
                  className="text-tripplo-200 font-medium"
                  href="mailto:support@tripplo.co?subject=Tripplo - Account Removal Request&body=Please supply your email address below. Account deletion requests can take up to 72Hrs to be processed. You will be contacted by a customer service agent to complete the deletion process.%0A%0A"
                >
                  Request account removal
                </a>
              </BodyOne>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
export default RedeemDriverInvite;
